import { QIcon_close_default } from '@repo/icons/index'
import { useEffect } from 'react'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { IC_CAF_MY_LEVEL, IC_CAF_MY_LEVEL_PC } from '../../../../../modules/images'
import urls from '../../../../../modules/urls'
import { COLORS } from '../../../../../modules/vars'
import { CAF_LEVEL_IMAGE, CAF_LEVEL_TAG, NUMS } from '../../../../../pages/[lang]/webview/caf/analysis/caf_level'
import { useCafSidePopup } from '../../common/CafSidePopup'

export interface SidePopupBodyProps {
  headerProps: SidePopupHeaderProps
  children: JSX.Element | JSX.Element[] | string | string[]
}
export interface SidePopupHeaderProps {
  title: string | JSX.Element
  onClose: () => void
  SubIcon?: any
  onClickSubIcon?: any
}
type AnalysisType = 'Complexity' | 'Accuracy' | 'Fluency' | null

const DEFAULT_PROPS = {
  hFull: true,
  bgWhite: true,
  maxW: 512,
  noTopSide: true,
  overlayClose: true,
}
const useCafLevelSidePopup = () => {
  const router = useAppRouter()
  const sidePopup = useCafSidePopup()
  const { t } = useTranslation()
  const { lessonId, tab, level } = router.query
  const analysisType = +tab === 1 ? 'Complexity' : +tab === 2 ? 'Accuracy' : +tab === 3 ? 'Fluency' : 'Pronunciation'
  const title =
    +tab === 1
      ? t(n_caf.level_detail.c.title)
      : +tab === 2
      ? t(n_caf.level_detail.a.title)
      : +tab === 3
      ? t(n_caf.level_detail.f.title)
      : t(n_caf.level_detail.p.title)
  const popupId = `caf_level_${analysisType}_${level}`
  const onClose = () => {
    router.push(urls.student.newPortal.lessons.reviewDetail.asCafLevel(lessonId, tab, null, analysisType))
  }
  useEffect(() => {
    if (+level >= 0) {
      sidePopup.show({
        ...DEFAULT_PROPS,
        id: popupId,
        body: (
          <SidePopupBody
            headerProps={{
              title: (
                <div className="flex flex-row items-center justify-start text-[20px] font-bold text-black">{title}</div>
              ),
              onClose: onClose,
            }}
          >
            <div className="flex w-full flex-col items-start justify-start pr-[68px]">
              <Content level={+level} analysisType={analysisType} />
            </div>
          </SidePopupBody>
        ),
        bgColor: COLORS.WHITE,
        noScrollbar: false,
        onClose: onClose,
      })
    }
  }, [level])
}

interface ContentProps {
  level: number
  analysisType: 'Complexity' | 'Accuracy' | 'Fluency' | 'Pronunciation'
}
const Content = ({ level, analysisType }: ContentProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  if (!level || !analysisType) return <div className="w-full break-all bg-gray-50 px-[12px]"></div>
  return (
    <div className="w-full">
      <div className="relative">
        <img
          src={isMobile ? IC_CAF_MY_LEVEL : IC_CAF_MY_LEVEL_PC}
          width={60}
          height={147}
          alt="score"
          className="absolute"
          style={{
            left: level >= 9 ? '85%' : `${(100 / 7) * (level - 3)}%`,
            bottom: '52px',
          }}
        />
        <img src={CAF_LEVEL_IMAGE[analysisType]} className="w-full" alt="level" />
      </div>
      <div className="text-14 mt-12 text-gray-700">
        {analysisType === 'Pronunciation' ? t(n_caf.level_detail.stat_info_p) : t(n_caf.level_detail.stat_info)}
      </div>
      <div className="h-32" />
      {NUMS.map((item, idx) => (
        <div className="border-b-1 flex justify-between gap-[21px] border-gray-200 py-[12px]" key={item}>
          <div className="shrink-0">
            <span className="text-16 font-bold">
              {t(n_caf.complexity.level_score)} {item}
            </span>
            <div className="text-12 mt-4 font-medium text-gray-700">{CAF_LEVEL_TAG[analysisType]['per'][idx]}%</div>
          </div>
          <span className="text-14 flex-grow text-left text-gray-700">
            {t(n_caf.level_detail[CAF_LEVEL_TAG[analysisType]['tag']][`level${item}`])}
          </span>
        </div>
      ))}
    </div>
  )
}

const SidePopupBody = ({ headerProps, children }: SidePopupBodyProps) => {
  return (
    <div className="relative flex w-[100%] flex-col items-start justify-start">
      <Header {...headerProps} />
      <div className="mt-[63px] flex min-h-full w-full items-start justify-start overflow-y-scroll px-[20px] pt-[20px]">
        {children}
        <div className="h-full"></div>
      </div>
    </div>
  )
}

const Header = ({ title, onClose, SubIcon }: SidePopupHeaderProps) => {
  return (
    <div className="z-9999 fixed top-0 left-0 flex w-full flex-row items-center justify-between border-b-[1px] border-gray-200 bg-white px-[20px] py-[16px]">
      <div className="font-bold">{title}</div>
      <div className="flex flex-row items-center justify-end">
        {SubIcon && <SubIcon width={24} height={24} className="cursor-pointer" />}
        <QIcon_close_default width={24} height={24} className="ml-[24px] cursor-pointer" onClick={onClose} />
      </div>
    </div>
  )
}

export default useCafLevelSidePopup
